/**
 * Wire and UI types for the Compare-to feature.
 *
 * The wire half mirrors `POST /api/v1/contracts/compare` in contractsapi. That
 * endpoint is a companion to the table's own paginated read: the client sends
 * the ids it already has on screen plus a timeframe, and gets the movement back.
 * It deliberately serves no sorting or filtering.
 */

/** Timeframes the compare endpoint accepts, as the literal wire values. */
export const COMPARE_PERIODS = [
  "last30Days",
  "mtd",
  "qtd",
  "ytd",
  "last12Months",
  "custom"
] as const;

export type ComparePeriod = (typeof COMPARE_PERIODS)[number];

/**
 * One default for all three tables, so Services does not behave differently
 * from Contracts and Companies. YTD, as the design specifies.
 *
 * A wider window reaches further past the point the snapshot tables began
 * recording, so more rows answer `existed: false`. That is a bigger population
 * than MTD reached, not a new failure mode: such a row renders `withdrawn`
 * (`deltaColumns.tsx`), a marker carrying its own explanation rather than an
 * unexplained blank, and New needs positive evidence from the row's own dates
 * (`resolveImpact`, on the `allowNew` grains), so a row predating the window
 * cannot be labelled New however far back it opens. Companies never shows New
 * at all.
 *
 * Deliberately thin on mechanism. Four earlier versions of this paragraph each
 * restated what those two functions do, and each was wrong on a different
 * detail; they are named here so the next reader checks them instead.
 */
export const DEFAULT_COMPARE_PERIOD: ComparePeriod = "ytd";

/**
 * The impact labels the API can emit. `new` never arrives for contracts — the
 * endpoint hardcodes `_ALLOW_NEW = False` for that grain — so it is derived on
 * the client instead; see `resolveImpact`.
 */
export type CompareImpact =
  | "new"
  | "churn"
  | "upsell"
  | "downsell"
  | "noChange";

export interface MetricDelta {
  previous: number | null;
  current: number | null;
  /** `current - previous`, or null when either side is absent. Not the same as 0. */
  delta: number | null;
  impact: CompareImpact;
}

/**
 * The metric keys the response is keyed by. They coincide with the property
 * names of the same figures on `ContractTableRow`, but nothing enforces that —
 * a consumer still pairs each key with the column it belongs beside by hand
 * (see `getCompareMetricSlots` in `ContractsTable`), because the column id
 * comes from `ContractColumn` and is free to be renamed independently.
 */
export const COMPARE_METRIC_KEYS = [
  "arr",
  "arrLocal",
  "carr",
  "carrLocal",
  "mrr",
  "mrrLocal",
  "cmrr",
  "cmrrLocal",
  "annualContractValue",
  "annualContractValueLocal",
  "totalContractValue",
  "totalContractValueLocal"
] as const;

export type CompareMetricKey = (typeof COMPARE_METRIC_KEYS)[number];

/**
 * The metrics the company-grain endpoint serves, in the order the Companies
 * table lists them.
 *
 * Four rather than twelve: `companies` carries no CARR/CMRR, and no terms
 * currency pair either — a company's figures sum across contracts that may
 * each be signed in a different currency, so "the currency of the row's own
 * terms" has no single answer. The story's `Δ [Metric] (Terms currency)` column
 * is therefore withheld on this table rather than fabricated.
 *
 * `satisfies` makes the compiler prove these are genuine `CompareMetricKey`s,
 * so a typo cannot introduce a fifth metric the response is never keyed by.
 */
export const COMPANY_COMPARE_METRIC_KEYS = [
  "arr",
  "mrr",
  "annualContractValue",
  "totalContractValue"
] as const satisfies readonly CompareMetricKey[];

export type CompanyCompareMetricKey =
  (typeof COMPANY_COMPARE_METRIC_KEYS)[number];

/**
 * ARR and CARR only — the endpoint serves the MRR/CMRR twins too, but this
 * table shows neither. Both carry a terms twin: a service has one currency.
 */
export const SERVICE_COMPARE_METRIC_KEYS = [
  "arr",
  "arrLocal",
  "carr",
  "carrLocal"
] as const satisfies readonly CompareMetricKey[];

export type ServiceCompareMetricKey =
  (typeof SERVICE_COMPARE_METRIC_KEYS)[number];

/**
 * The part of a comparison the Δ and Impact columns actually read.
 *
 * Both grains' responses satisfy this, which is what lets one set of column
 * builders serve the Contracts and Companies tables. The row's own identifier
 * is deliberately absent: the columns look rows up through a map the caller
 * supplies, so they never need to know whether the id field is `contractId` or
 * `companyId`.
 */
export interface RowComparison {
  /**
   * Whether the row had any recorded figure at the period start. False is the
   * single shared reason every metric on the row reports a null delta, so it is
   * worth surfacing separately from a per-metric absence.
   */
  existed: boolean;
  /**
   * The currency the account-currency figures are denominated in, where the
   * grain reports one. Optional because the company-grain endpoint sends no
   * `currency` field at all — a company's contracts can each be signed in a
   * different currency, so there is no single one to report. Declaring it
   * required here would promise `string | null` for a value that is actually
   * `undefined` on every company row.
   */
  currency?: string | null;
  /**
   * The row's currency at the period start, where the grain has one. Absent at
   * company grain, which carries no terms currency to change.
   */
  previousCurrency?: string | null;
  metrics: Partial<Record<CompareMetricKey, MetricDelta>>;
}

export interface ContractComparison extends RowComparison {
  contractId: string;
  previousCapturedAt: string | null;
  /**
   * Required here where the base type leaves it optional: this endpoint always
   * reports the contract's own currency.
   */
  currency: string | null;
  /**
   * The contract's currency at the period start. When it differs from
   * `currency`, the terms-currency figures on either side of the subtraction
   * are denominated differently and their delta is not a single figure.
   *
   * Required here where the base type leaves it optional: this grain always
   * reports it, and the terms-currency columns depend on it being present.
   */
  previousCurrency: string | null;
}

/**
 * One company's movement over the period, from
 * `POST /api/v1/contracts/companies/compare`.
 *
 * No `previousCurrency`: a company holds contracts in potentially several
 * currencies, so the endpoint serves account-currency figures only.
 */
export interface CompanyComparison extends RowComparison {
  companyId: string;
  previousCapturedAt: string | null;
  /**
   * What moved the previous figure into place — an amendment, or a sweep such
   * as `nightly_sweep` picking up a contract that expired overnight. Carried so
   * a fall caused by expiry is distinguishable from one caused by an amendment.
   */
  previousSource: string | null;
}

/**
 * One service's movement over the period, from
 * `POST /api/v1/contracts/services/compare`.
 */
export interface ServiceComparison extends RowComparison {
  revenueItemId: string;
  previousCapturedAt: string | null;
  /** What moved the previous figure into place, e.g. `service_edit`. */
  previousSource: string | null;
  /** Required here: a service has one contract, so its currency is always known. */
  currency: string | null;
  /** When this differs from `currency`, the two terms figures are not subtractable. */
  previousCurrency: string | null;
  /**
   * Contract financials set by hand, so service figures will not sum to the
   * contract total. Carried for a table that shows both; this one does not yet.
   */
  contractHasFinancialOverride: boolean;
}

export interface CompareToResponse {
  period: string;
  periodStart: string;
  contracts: ContractComparison[];
}

export interface CompareToRequest {
  contractIds: string[];
  period: ComparePeriod;
  /** ISO date (`YYYY-MM-DD`). Required when `period` is `custom`, ignored otherwise. */
  customStart?: string;
}

export interface CompanyCompareToResponse {
  period: string;
  periodStart: string;
  companies: CompanyComparison[];
  /**
   * Ids the endpoint could not resolve to a company.
   *
   * Reported rather than silently dropped, which is what the contract-grain
   * endpoint does — a request naming only unknown ids answers `200` with an
   * empty list there, indistinguishable from "nothing moved". A company
   * deleted between the page load and this call is ordinary, so it is not an
   * error; it just must not render as no change.
   */
  notFoundCompanyIds?: string[];
}

export interface CompanyCompareToRequest {
  companyIds: string[];
  period: ComparePeriod;
  /** ISO date (`YYYY-MM-DD`). Required when `period` is `custom`, ignored otherwise. */
  customStart?: string;
}

/**
 * What the Display menu holds. A custom range carries only its start: the
 * endpoint compares against live values, so the "to" side is always now.
 */
export interface CompareToSelection {
  period: ComparePeriod;
  customStart?: Date;
  /**
   * The end date the user picked in the calendar. Never sent, and deliberately
   * not shown in the column header either — the comparison always runs to
   * today, so a header ending on this date would misstate its own range. It
   * exists solely so the date picker re-opens on the range the user chose.
   */
  customEnd?: Date;
}

/** The endpoint rejects a request carrying more ids than this. */
export const COMPARE_MAX_IDS_PER_REQUEST = 200;
